import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, alpha, useMediaQuery, useTheme } from '@mui/material'

import { useAmbientSurge } from 'layouts/LayoutAuthAmbient/ambientSurge'
import { zoomOf } from 'utils/textZoom'

/**
 * Ambient auth background: purple blobs ORBITING the page, each breathing on
 * its own rhythm, over a film-grain overlay. GPU-cheap (transform / filter /
 * opacity only) and disabled under prefers-reduced-motion.
 *
 * ── Relationship to `apps/app`'s `AuthBackdrop` ───────────────────────────
 *
 * The two are the same DESIGN and deliberately not the same COMPONENT. What is
 * shared is everything a user could notice across the two products: the purple
 * FAMILY, the grain's character, and the rule that no length is a px literal.
 * What differs is mechanism, and each difference is a measured local answer to
 * a condition the other surface does not have. Those are called out at the
 * value they belong to; the short list is:
 *
 *   - PER-BLOB blur here vs one container blur there. Kept — this composition
 *     has a job the phone's does not (see `accent`).
 *   - `multiply` on light here vs a per-blob opacity gain there. Kept — see
 *     `blendFor`. The app's `LIGHT_GAIN` deliberately does NOT port.
 *   - Orbit + breath + surge here vs a single drift there. Kept.
 *   - Different vw/vh coefficients. NOT a different rule — the same rule
 *     re-fitted to a desktop frame, see `VW_COEFF`.
 *
 * ── Why the motion is shaped the way it is ────────────────────────────────
 *
 * AMPLITUDE, NOT SPEED (VERA-1095). The original blobs travelled 80px each.
 * That is smaller than their own blur radii (150px and 250px), and a blob
 * displaced by less than its blur radius cannot read as moving — the soft edge
 * smears the displacement below the threshold where the eye can find an edge
 * to track. Speeding that up would have produced the same invisible
 * displacement, sooner. Travel is now an ORBIT whose radius is 2-3x each
 * blob's blur radius.
 *
 * ORBIT + BREATH ARE TWO ANIMATIONS ON TWO ELEMENTS, deliberately. They want
 * different easings and a single element can only have one timing function per
 * animation: the orbit wants `linear`, because a closed loop under ease-in-out
 * visibly stalls at each keyframe and reads as lurching rather than circling;
 * the breath wants `ease-in-out`, because a scale/opacity pulse under linear
 * reads mechanical. So the OUTER box orbits and the INNER box breathes. Their
 * periods are also mutually non-harmonic, so a blob is never at the same point
 * of its orbit at the same point of its breath twice — which is what stops the
 * composition looking like a loop.
 *
 * PHASE CONTINUITY ACROSS ROUTES (VERA-1095). Sign-in and sign-up are sibling
 * routes, so navigating between them UNMOUNTS this component and mounts a
 * fresh one — every CSS animation restarted at 0% and the whole field visibly
 * snapped back mid-cross-fade. The fix is a negative `animation-delay`
 * computed from a module-level epoch: a newly-mounted node starts at exactly
 * the phase the old one had reached, so the swap is invisible.
 *
 * The alternative was hoisting this above the route boundary so it never
 * unmounts. That was rejected: each auth page owns its own `position: fixed`
 * OPAQUE shell, so a hoisted background would be painted over unless all four
 * shells became transparent — and those shells are exactly what the sign-in
 * <-> sign-up cross-fade depends on being identical (see
 * `useAuthPageTransition`). Phase continuity gets the same visible result
 * without touching that coupling, and it works for any page that renders this.
 *
 * SIZED AS ATMOSPHERE, NOT AS A LIGHT SOURCE (2026-08-25). The blobs were
 * 1200/1800/820px at blur 150/250/130 — big enough and bright enough that each
 * one read as a distinct glowing object rather than as depth behind the page.
 * Now 900/1300/640 at blur 200/300/170, i.e. SMALLER and MORE blurred, which is
 * the combination that turns a shape into a field: a blob whose blur radius is
 * a large fraction of its own size has no discernible edge left at all.
 *
 * ⚠️ LEGIBILITY IS NOT STRUCTURAL HERE, WHATEVER THIS DOCBLOCK USED TO SAY.
 *
 * The claim that stood here was that every core sits off-screen and that "orbit
 * radii are held well below each blob's own off-screen offset, so no core can
 * swing into the centre where the form sits". It is comforting and it is false,
 * and it matters because it is the sentence that would tell a future reader
 * they do not need to measure. MEASURED, sweeping real time and reading each
 * centre as a viewport fraction:
 *
 * | blob   | centre y range     | on-page?                                |
 * |--------|--------------------|-----------------------------------------|
 * | top    | -0.881 .. -0.194   | no, but only 155px clear at 800h        |
 * | bottom |  0.868 .. 1.857    | YES — swings to 87% down the page       |
 * | accent |  0.252 .. 1.228    | yes, always partly on (and always was)  |
 *
 * The bottom blob's orbit radius (400) is LARGER than its off-screen offset
 * (290 at the design frame), so a quarter of every 46s revolution puts its core
 * inside the viewport — and it arrives just below the footer link, which is the
 * one row on this page with almost no contrast margin. That is not a bug to fix
 * by moving it; it is the approved composition, and the wash reads the way it
 * does partly because of that pass. It is a reason the alphas MUST be fitted
 * against a measurement rather than against this paragraph.
 *
 * Note for anyone re-measuring: freezing every animation at the same FRACTIONAL
 * phase is not a worst-case search. The six periods (orbits 34/46/27, breaths
 * 19/26/15) are deliberately non-harmonic, so they drift against each other and
 * the composition reaches states no single fraction produces. Sweep wall-clock
 * TIME. Doing it the wrong way here reported the light-mode footer link at
 * 4.95:1; the real worst case over time is 4.83:1.
 */

/* ---------------------------------------------------------------------------
 * Geometry — proportional, because px literals are lengths on ONE frame
 *
 * Every length below is a RATIO of `FRAME`, resolved through `SCALE()`, and
 * every position is a CENTRE expressed as a fraction of the viewport. No px
 * literals. This mirrors `apps/app`'s `AuthBackdrop`, and it is here for the
 * same reason rather than for consistency's sake: the values were authored
 * against one window size and were silently wrong at every other one.
 *
 * MEASURED before this change, at the two ends of the desktop range: the
 * `bottom` blob is 1300px, which is 102% of a 1280-wide viewport and 51% of a
 * 2560-wide one. Same for the orbit — a 400px orbit is a wander at 1280 and a
 * twitch at 2560 — and for the blur, which is what keeps a circle reading as a
 * wash rather than as a disc. The composition therefore tightened into three
 * small, comparatively crisp discs as the window grew, which is not the design
 * that was approved at any size.
 * ------------------------------------------------------------------------- */

/**
 * The frame the absolute lengths were authored against: a laptop.
 *
 * This is the fact a px transcription loses. 900/1300/640, blur 200/300/170,
 * orbits 340/400/430 are all lengths *on this frame* — ratios that happened to
 * be written out at one window size.
 *
 * NOT the phone frame `apps/app` uses. `apps/platform` is a desktop console and
 * is never rendered at 375 wide, so anchoring to a phone would fit the blend
 * (below) to a frame this app does not have.
 */
const FRAME = { w: 1280, h: 800 } as const

/**
 * The viewport-unit blend that replaces the px literals. `SCALE(1)` is 1280px
 * at the design frame.
 *
 * ## The criterion — identical to `apps/app`'s, and it is the criterion that
 * ## is shared, not the numbers
 *
 * These blobs are a background WASH, and what is perceived of a wash is the
 * fraction of the FRAME it covers — not its width, not its height. Preserving
 * that means scaling every length by sqrt(W*H), the geometric mean of the two
 * axes. CSS has no portable sqrt(vw*vh), so this approximates it with a linear
 * blend of the two axes fitted at two anchor frames.
 *
 * ## Why the coefficients differ from `apps/app`'s 34.9 / 30.06
 *
 * They are the SAME rule with a different anchor, not a different rule. The
 * app's pair is normalised so `SCALE(1)` is 375 — its phone frame — and fitted
 * at 375x812 and 1440x900. This pair is normalised to 1280 and fitted at
 * 1280x800 and 2560x1440, because that is the range this app is actually used
 * in. Re-fitting was checked rather than assumed, against the equal-area
 * target sqrt(W*H) * 1280/sqrt(1280*800):
 *
 * | frame      | this fit | app's pair, renormalised |
 * |------------|----------|--------------------------|
 * | 1280x800   |  +0.00%  |  -0.07%                  |
 * | 1440x900   |  +0.00%  |  +1.65%                  |
 * | 1920x1080  |  +0.00%  |  +1.65%                  |
 * | 2560x1440  |  +0.00%  |  +1.65%                  |
 * | 1024x768   |  +0.66%  |  +2.3%                   |
 * | 3440x1440  |  +1.48%  |  +8.0%                   |
 *
 * Modest at common sizes and material on ultrawide — which is a real desktop
 * frame and not a real phone one. It fails soft in the same direction the app's
 * does: a slightly large wash at pathological aspect ratios (+5% at 1280x400,
 * +11% at 800x1200), never a broken composition.
 *
 * Do not "unify" these with the app's pair. Sharing the numbers across two
 * different design frames would mean one of the two apps is no longer rendering
 * what was approved for it.
 */
const VW_COEFF = 48.6833
const VH_COEFF = 82.1067

/** `ratio` of the design frame's width, as a viewport-relative CSS length. */
const SCALE = (ratio: number): string => {
  const w = ratio * VW_COEFF
  const h = ratio * VH_COEFF
  return `calc(${w.toFixed(4)}vw ${h < 0 ? '-' : '+'} ${Math.abs(h).toFixed(4)}vh)`
}

/**
 * One purple family, and it is the SAME family `apps/app` settled on:
 * #8F68EA / #4C2FA8 / #6E4FD0, hues 258 / 254 / 254 degrees. A warmer violet
 * was tried there during design and rejected as reading too magenta — do not
 * "warm these up".
 *
 * This replaced a set that had drifted out of that family, most obviously in
 * light mode: the accent's #9333EA measures 271.5deg, i.e. 13-17deg warmer than
 * everything else on either surface, and is precisely the magenta-leaning
 * violet the app rejected. The three now differ in LIGHTNESS only, which is
 * what gives the field depth without a hue shift.
 *
 * Colour is one set for both modes. It used to be two — a `color` and a
 * `colorLight` — on the reasoning that light mode needs more saturation to
 * register. That reasoning is sound and is now carried by `blendFor` instead:
 * `multiply` is what makes light mode read as colour, and once the blend does
 * that job a second palette is just a second place for the hue to drift. The
 * per-mode difference that remains is ALPHA, which is a quantity, not a hue.
 */
const PURPLE = {
  /** Lightest — luminance variety without a hue shift. */
  light: '#8F68EA',
  /** Deepest. Carries the large bottom field. */
  deep: '#4C2FA8',
  mid: '#6E4FD0',
} as const

/**
 * ⚠️ DO NOT "MATCH" THESE TO vera-website's `--field-glow-*`. THAT REPO IS
 * DOWNSTREAM OF THIS ONE AND ITS DARK COPY IS STALE. (VRG-620, 2026-09-21.)
 *
 * `vera-website/app/tokens.css` carries a three-stop ramp whose header says it
 * is "the auth background's dark stops verbatim". It was true for 65 minutes.
 * Measured, by commit timestamp:
 *
 *   2026-09-03 03:55  vera-website 0889503  ports the stops then on main here
 *   2026-09-03 05:00  vera-2       44ebd0c13 swaps this palette to the app's
 *
 * So the website's DARK ramp is this file's PRE-swap palette, exactly — and
 * the relative luminances confirm it rather than the timestamps alone:
 *
 * | website dark stop | = this file's OLD stop | L      | current stop     |
 * |-------------------|------------------------|--------|------------------|
 * | 111, 54, 203      | #6F36CB                | 0.1033 | #6E4FD0 (0.1346) |
 * | 127, 72, 223      | #7F48DF                | 0.1447 | #4C2FA8 (0.0640) |
 * | 143,104, 234      | #8F68EA                | 0.2168 | #8F68EA (same)   |
 *
 * Those match the docblock table above (.1034 / .1444) to four decimals.
 * "Matching" this file to the website would therefore REVERT 44ebd0c13 —
 * undoing the move onto `apps/app`'s family, which is the thing that makes
 * this surface and the phone app the same design.
 *
 * The website's LIGHT ramp is a separate trap in the same direction: its third
 * stop is 147,51,234 = #9333EA, which measures 271.5deg. That is the exact
 * value, to the degree, that the note above records as "13-17deg warmer than
 * everything else on either surface, and precisely the magenta-leaning violet
 * the app rejected". Adopting it would re-introduce a colour this file removed
 * on measurement.
 *
 * ── THE FIELDS GENUINELY DO NOT LOOK ALIKE, AND THE STOPS ARE NOT WHY ─────
 *
 * Both fields measured with ONE instrument (full-frame mean over a wall-clock
 * sweep, composited frames off a real render, form masked out):
 *
 * | full-frame mean   | this file   | /enter      | ratio |
 * |-------------------|-------------|-------------|-------|
 * | dark  chroma      | 11.5 - 11.8 | 22.8        | 1.9x  |
 * | dark  dL*         |  1.9 -  2.0 |  4.5        | 2.3x  |
 * | light chroma      |  5.7 -  9.6 | 20.2        | 2.1x  |
 * | light dL*         |  3.4 -  5.3 |  7.8        | 1.5x  |
 *
 * `/enter` really is about twice as present. But the raw chroma of the two
 * stop sets differs by only ~1.17x (149/151/130 there against 129/121/130
 * here), so the stops account for a small part of it. The rest is MECHANISM:
 * `/enter` draws three unblurred percentage-sized gradients with cores just
 * off each edge, so most of their energy lands on the page, while these blobs
 * spend theirs in a 200-300px blur with two of three cores off-viewport.
 *
 * Which means the gap CANNOT be closed by swapping stops, and was measured
 * rather than assumed. Raising only the dark alphas, the lever that costs no
 * hue and has the contrast headroom (dark's tightest row sits at 6.76:1
 * against a 4.5 threshold):
 *
 * | dark alphas          | chroma | dL*  | worst text | note              |
 * |----------------------|--------|------|------------|-------------------|
 * | 0.34/0.58/0.18 (now) |  11.5  | 1.92 |   6.76:1   | shipped           |
 * | 0.55/0.90/0.30       |  17.0  | 3.43 |   6.03:1   |                   |
 * | 0.70/1.00/0.38       |  19.1  | 4.08 |   5.71:1   | ceiling: 1.00 cap |
 * | /enter                  22.8  | 4.48 |     -      | target            |
 *
 * Even with the bottom blob at alpha 1.0 this reaches 84% of the chroma and
 * 91% of the dL*. So alpha cannot close it either; only geometry could, and
 * that would retire "sized as atmosphere, not as a light source" above.
 *
 * ⚠️ AND LIGHT MODE CANNOT MOVE AT ALL. `/enter`'s own file records its light
 * field as OVER the contrast ceiling — 4.50:1 at 1280 and 4.45:1 at 375 on its
 * PASSWORD label, both under the 4.5 minimum, flagged there as an unresolved
 * product call. This page's tightest light row measures 4.82:1. Porting
 * `/enter`'s light presence would trade 0.32 of real margin for a known
 * failure, behind a login form.
 *
 * Nothing here was changed on those grounds. The one value that did move is
 * the dark GRAIN opacity, and it moved because this page's own banding
 * measurement asked for it — see the overlay element at the bottom of the file.
 */

/**
 * Dark ADDS light to a near-black page; light SUBTRACTS from a near-white one.
 *
 * On the dark page a blob adds luminance to #09090b, where there is a full
 * range of headroom to climb into, so it reads immediately. On the light page
 * (#f4f5f5) there is almost no headroom left above the backdrop — adding light
 * to near-white does close to nothing, which is why a straight port of the dark
 * values reads as a pale, dirty haze rather than as colour.
 *
 * ## Why this and NOT `apps/app`'s per-blob `LIGHT_GAIN`
 *
 * The app hits the identical problem and answers it differently: it keeps
 * `normal` blending in both modes and multiplies light-mode opacity by a fitted
 * per-blob gain of [1.5, 1.4, 1]. That gain must not be ported here, and the
 * reason is not preference:
 *
 *   - It is a fitted CONSTANT correcting for a blend mode that does the wrong
 *     thing on a light ground. `multiply` removes the problem instead of
 *     compensating for it, so there is no constant to re-fit when the ground,
 *     the palette or the geometry moves — and all three have moved here.
 *   - Stacked on `multiply` it would be actively wrong. MEASURED on this page
 *     before this change, light mode was already the MORE present of the two
 *     modes by a factor of ~3.8 in perceptual lightness spread (dL* 19.1 light
 *     vs 5.0 dark). A 1.4-1.5x gain on top would push the ground under the
 *     footer link past the point where 14px text clears 4.5:1 — it measured
 *     4.88:1 before this work, with 0.38 of margin.
 *
 * The app's own note says the same thing from the other side: its gain is
 * capped at ~1.0 on one blob by a single 16px word. A mechanism that is already
 * pinned by its worst case is not one to import into a surface that has a
 * structural alternative.
 */
const blendFor = (mode: 'light' | 'dark') => (mode === 'light' ? 'multiply' : 'normal')

interface Blob {
  key: string
  /** Diameter, in design-frame px. Resolved through `SCALE`. */
  size: number
  /**
   * The blob's CENTRE, as a fraction of the viewport.
   *
   * NOT a CSS edge offset. An edge anchor is coupled to the diameter twice
   * over — `bottom: -940px` only puts the centre where it belongs while the
   * blob is exactly 1300px — so the moment sizing became proportional every
   * edge offset silently stopped meaning what it said. `apps/app` hit exactly
   * this and it cost a correction cycle there; a centre holds the composition
   * at every frame because it is the thing the composition is actually made of.
   *
   * Evaluated so that 1280x800 renders what it rendered before: these are, by
   * construction, the centres the old edge anchors produced at that frame.
   */
  centre: { x: number; y: number }
  /**
   * OPTION B (2026-09-23): offset of this blob's centre from the VERA logo's
   * centre, in design-frame px (so it scales with `SCALE` like everything
   * else). When set, `centre` is only the fallback used before the logo is
   * found. See ANCHOR below.
   */
  fromLogo?: { x: number; y: number }
  /** Blur radius, in design-frame px. An attribute of the blob, so it scales. */
  blur: number
  /** Orbit radius in design-frame px, and seconds for one full revolution. */
  orbit: {
    radius: number
    period: number
    reverse?: boolean
    /** Radius swings by ±amp, `k` times per lap (2026-09-23). Lobes sharing the
     *  path at thirds of a lap sit at DIFFERENT radii at any instant, so they
     *  drift toward and away from each other as the triangle turns. */
    wobble?: { amp: number; k: number }
  }
  /** Scale/opacity pulse — period intentionally non-harmonic with the orbit. */
  breath: { from: number; to: number; opacityFrom: number; opacityTo: number; period: number }
  /** Silhouette melt period (border-radius + slow turn), seconds. 2026-09-23. */
  morph: number
  /** Fraction of the orbit this blob starts ahead by (0..1). Lobes sharing an
   *  orbit and period at phases 0, 1/3, 2/3 stay a fixed third of a lap apart —
   *  a rigid, rotating triangle. 2026-09-23. */
  phase?: number
  /**
   * Stop opacity per mode. See `PURPLE` for why the COLOUR no longer differs
   * per mode and only this does, and the note below for how these were fitted.
   */
  alpha: { dark: number; light: number }
  color: string
}

/**
 * ALPHA MUST RISE WITH BLUR, FALL WITH SIZE, AND RISE AS THE STOP DARKENS.
 *
 * A revision in 2026-08 changed three things in one move — the blobs got
 * SMALLER (1200/1800/820 -> 900/1300/640), MORE BLURRED (150/250/130 ->
 * 200/300/170) and DIMMER (0.22/0.26/0.13 -> 0.16/0.19/0.09) — under the correct
 * intent of reading as atmosphere rather than as a light source. The intent was
 * right and the arithmetic was backwards, and Spencer's read was that the blobs
 * had been "wiped from the page". Blur redistributes a fixed amount of colour
 * over a larger area and shrinking the blob shrinks the supply of it, so both
 * of those already dim it; cutting alpha on top made three reductions that
 * MULTIPLY. The alphas that ANSWERED that — 0.30 / 0.34 / 0.16 dark — are the
 * approved state, and they are the baseline everything below is measured
 * against. Do not read the history as "dark wanted to be brighter"; dark had
 * already been fixed.
 *
 * The palette swap to the app's family is the same trap wearing a third face,
 * so the arithmetic is written out. On the dark page a blob's contribution
 * goes as alpha * (L_stop - L_base), L_base = 0.0028:
 *
 * | blob   | old stop        | new stop        | alpha to hold LUMINANCE |
 * |--------|-----------------|-----------------|-------------------------|
 * | top    | #6F36CB L=.1034 | #6E4FD0 L=.1351 | 0.30 -> 0.23            |
 * | bottom | #7F48DF L=.1444 | #4C2FA8 L=.0640 | 0.34 -> 0.79            |
 * | accent | #8F68EA L=.1351 | #8F68EA (same)  | unchanged               |
 *
 * ## The arithmetic is necessary and NOT sufficient — it overshot by 59%
 *
 * That table was implemented first and MEASURED, and 0.79 on the bottom blob
 * was far too much: mean composited chroma came out at 28.1 against the
 * approved state's 17.6. Holding LUMINANCE is not the same as holding
 * PRESENCE, because #4C2FA8 is more saturated relative to its lightness than
 * the #7F48DF it replaced — so the alpha that restores the light it lost
 * oversupplies the colour. Two quantities, one lever, and the arithmetic only
 * tracks one of them.
 *
 * So the values below are FITTED against measurement, on two figures that a
 * contrast ratio alone cannot see:
 *
 *   - dL*, how far the wash moves the page in a perceptually uniform scale.
 *     Contrast ratio is not one — 1.10 on a near-black page and 1.10 on a
 *     near-white page are wildly different amounts of visible movement, which
 *     is why the two modes were 3.8x apart without anyone noticing.
 *   - mean CHROMA (rgb excursion), what makes the wash read as purple rather
 *     than as grey. This is the one the first pass lost: dropping light-mode
 *     alpha AND swapping to a less saturated stop was, once again, two
 *     reductions that multiply, and light came out visibly grey.
 *
 * | measured, same instrument      | approved    | shipped     |
 * |--------------------------------|-------------|-------------|
 * | dark  dL* / chroma             | 9.1 / 17.6  | 10.2 / 21.7 |
 * | light dL* / chroma             | 18.0 / 24.3 | 20.5 / 18.7 |
 * | light worst text, over time    | 4.74:1      | 4.83:1      |
 * | dark  worst text, over time    | 6.68:1      | 6.56:1      |
 *
 * Light improves on every text row; dark gives up ~0.12 uniformly, against a
 * 4.5 threshold it clears by 46%. Both are worst-case over a wall-clock sweep,
 * not over a fractional-phase one — see the note at the end of the docblock for
 * why that distinction is worth 0.12 on its own.
 *
 * Dark moves up slightly and light moves down slightly, which is deliberate:
 * they were 38% apart in chroma in one direction and are now 13% apart in the
 * other. The light chroma drop is the honest, unavoidable cost of the palette
 * — reaching the old 24.3 with these stops measured at 4.69:1 on the footer
 * link, i.e. below both the 4.80 baseline and, at that point, worth failing
 * for. The compensation is spatial instead: light's `top` alpha is high (0.80)
 * because that blob's presence lands where the page has contrast headroom,
 * while `bottom` stays low because it sits under the footer link. Same
 * per-blob logic as `apps/app`'s `LIGHT_GAIN`, reached through alpha rather
 * than through a gain on top of a blend that needed correcting.
 *
 * If you change `blur`, `size` or `color` here, redo the arithmetic above,
 * then MEASURE the composited ground — not the token, the COMPOSITE of base
 * plus three blurred blobs plus a blended grain, which no computed style
 * reports and which has to come off a rendered frame. Then LOOK at the page.
 * None of this is checkable by any automated test: every value here is valid
 * CSS and the build stays green whether the backdrop is visible or invisible.
 */
/*
 * ── OPTION B: ONE HALO BEHIND THE LOGO (2026-09-23, evening) ─────────────
 * Spencer chose B over the edge-anchored field: "center line behind Vera… then
 * there's a ton of negative space". The page is black; the VERA mark is the
 * light source. Three separated lobes rotate round the logo (see BLOBS). vera.inc /enter is still edge-anchored (A)
 * FOR COMPARISON ONLY — once B is chosen, /enter follows.
 *
 * WHY THE LOGO IS MEASURED, NOT ASSUMED. This background is shared by every
 * auth route (LayoutAuthAmbient) and the logo is not at a fixed height:
 * sign-in / forgot-password put it 283px above the viewport centre, sign-up
 * 153px, at every size tried (1280x800 .. 1920x1080, 390x844). A percentage
 * would miss on one page or the other, so `useLogoAnchor` finds
 * `[data-auth-logo]` and writes its centre into --ax/--ay. The swirl's pivot
 * moves there too, so the success surge tightens around the halo instead of
 * collapsing toward an empty screen centre.
 *
 * ── AMORPHOUS, LATER THE SAME DAY ──────────────────────────────────────────
 * The disc version below filled the black: negative space outside the column
 * measured 3.3% against 23.7% on the pre-retune page Spencer liked ("here they
 * all blend and it becomes a gradient-shifting background"). Now: smaller
 * blobs, blur small relative to size, fill solid to `closest-side`, and a
 * border-radius melt (`morph`). Dark negative space 27.4% here AND on /enter;
 * peak chroma 47.8 / 45.0; worst-frame text contrast 4.84 dark / 5.04 light.
 * The numbers in BLOBS are now the amorphous ones; the history below explains
 * the placement, which did not change in kind.
 *
 * ── RE-TUNED 2026-09-23 TO vera.inc /enter's COMPOSITION AND STRENGTH ──────
 * Spencer, comparing the two side by side: /enter's static field was "more
 * legible, easier to see", but he wanted it "more dynamic — morphing, liquid
 * blobs like we have on the current sign-in page". So the two pages now share
 * ONE field: /enter's placement and strength, this file's motion. vera.inc
 * /enter runs these exact numbers in pure CSS (app/enter/enter-ambient.css);
 * change one, change the other.
 *
 * WHAT MOVED. The top and bottom blobs sat 0.46 and 0.36 of a viewport OFF the
 * page, so only their fringes showed; /enter anchors its glows AT the edges.
 * Centres now: top (0.5, -0.24), bottom-RIGHT (0.86, 1.26), accent bottom-LEFT
 * (0.08, 1.1) — the accent moved OFF the column it used to sit behind. Orbit
 * radii came down (340/400/430 -> 140/110/120) so each glow drifts around its
 * anchor instead of sweeping the viewport; periods and breaths are unchanged.
 *
 * MEASURED, CIELAB chroma averaged over 20s, 1280x800 (/enter static -> here):
 *   dark   top-centre 35.0 -> 36.9   top corners 23.5 -> 21   bottom-right 25.2 -> 25.3
 *   light  top-centre 26.6 -> 26.5   (before: dark 13.7, 7.5, 14.1 — 2-3x weaker)
 * Matched again at 1920x1080 within ~4 per region.
 *
 * ⚠️ The per-blob light/dark alpha NOTES below predate this and describe the
 * old positions; their reasoning about per-blob tuning still holds, their
 * numbers do not. The light-mode text contrast they tuned against is now
 * carried by the READING WASH (see the element below the blobs).
 */
/*
 * THREE LOBES, SEPARATED — A ROTATING TRIANGLE ROUND THE LOGO (2026-09-23).
 *
 * Spencer, on the free-orbit version: "separate the blobs a little bit more…
 * right now they all overlap and just look like one colour. Sort of like what
 * we do on Vergence." Vergence's sign-in (apps/vergence-app auth layout, ported
 * to vergence-website /access as EmberBackdropV1) puts three DIFFERENT-colour
 * blobs at the corners of a triangle inside a container that rotates as one
 * piece, so they never meet; each only pulses in place.
 *
 * Same idea, without the container: a rotating wrapper would be a new stacking
 * context, and light mode's `multiply` would then blend against nothing (see
 * the compression wrapper's note) and fall back to haze. Instead all three
 * share ONE circular orbit round the logo — same radius, same period — at
 * phases 0, 1/3, 2/3. That IS a rigid rotating triangle, and the blend
 * target is untouched. Individuality comes from each lobe's own breath and
 * morph, on unrelated periods, which is Vergence's per-blob pulse.
 *
 * Separation by construction: vertices sit 170 · √3 ≈ 294 frame-px apart;
 * the lobes are 300 / 270 / 240 with blur ≤ 50, so their cores never touch
 * and their fringes only kiss. The three colours read as three colours.
 */
/*
 * BIGGER, CENTRED ON THE FORM, AND THE SWELL TRAVELS (2026-09-23, later).
 * Spencer: "move the blobs down… centred in the whole section", "make them a
 * little bigger each", and "I do like that they get small and then grow…
 * they feel continuous between them". So:
 *   · anchor = the whole form column's centre (see useLogoAnchor);
 *   · lobes 420 / 380 / 340 on an orbit of 250 — vertices 250·√3 ≈ 433 apart,
 *     still clear of each other at rest;
 *   · ONE breath period for all three (12s), offset by the same thirds as the
 *     orbit. The swell therefore travels round the triangle — one lobe peaks,
 *     then the next — which is the continuity he described, made deliberate.
 *     It also means neighbours are never at full size together, so the
 *     separation holds at the peak too. Morph periods stay unrelated.
 */
/*
 * CLOSE AND AWAY, NEVER FULLY APART (same evening). Spencer, on the 250-radius
 * version: "too separated — they should drift close and away from each other…
 * but always have some overlap so there isn't too much negative space in the
 * middle", and "I liked the blobs a little smaller". So the lobes go back to
 * 300 / 270 / 240 and the shared orbit WOBBLES: radius 150 ± 50, twice a lap.
 * Because the three sit a third of a lap apart, at any instant they are at
 * different radii (100..200): one tucks into the middle while another swings
 * out, so pairs part and rejoin.
 *
 * FITTED, NOT GUESSED. Simulated over three orbit periods WITH the travelling
 * breath (cores only, blur ignored), share of time with n neighbour pairs
 * overlapping, n = 0 / 1 / 2 / 3:
 *   radius 125 ± 55   0 /  0 / 38 / 62   <- all three merged most of the time
 *   radius 150 ± 50   0 / 37 / 61 /  2   <- shipped
 *   radius 150 ± 90   0 / 54 / 46 /  0
 * Never zero: the centre never empties. Rarely all three: they stay three
 * colours. The blur makes the real image read slightly more joined.
 */
/*
 * ×1.33, TOGETHER (same evening): the liquid-glass card on SignInPage is
 * ~476 × 650 frame-px, and at radius 150 the lobes lived almost entirely
 * behind it — frosted into a smear. Orbit AND lobe sizes AND blur scale by
 * the same 4/3, so the lobes now ride out round the card's edges while the
 * overlap mix fitted above (0 / 37 / 61 / 2 %) is UNCHANGED: that mix depends
 * only on the ratio of spacing to size, and the breath is dimensionless.
 */
const LOBE_ORBIT = { radius: 200, period: 64, wobble: { amp: 67, k: 2 } } as const
const LOBE_BREATH = { from: 0.82, to: 1.16, opacityFrom: 0.65, opacityTo: 1, period: 12 } as const
const BLOBS: Blob[] = [
  {
    key: 'mid',
    size: 400,
    centre: { x: 0.5, y: 0.5 },
    fromLogo: { x: 0, y: 0 },
    color: PURPLE.mid,
    alpha: { dark: 0.85, light: 0.95 },
    blur: 69,
    orbit: LOBE_ORBIT,
    phase: 0,
    breath: LOBE_BREATH,
    morph: 17,
  },
  {
    key: 'deep',
    size: 360,
    centre: { x: 0.5, y: 0.5 },
    fromLogo: { x: 0, y: 0 },
    color: PURPLE.deep,
    alpha: { dark: 0.95, light: 0.5 },
    blur: 72,
    orbit: LOBE_ORBIT,
    phase: 1 / 3,
    breath: LOBE_BREATH,
    morph: 23,
  },
  {
    key: 'light',
    size: 320,
    centre: { x: 0.5, y: 0.5 },
    fromLogo: { x: 0, y: 0 },
    color: PURPLE.light,
    alpha: { dark: 0.62, light: 0.7 },
    blur: 64,
    orbit: LOBE_ORBIT,
    phase: 2 / 3,
    breath: LOBE_BREATH,
    morph: 19,
  },
]

/** `#rrggbb` + alpha -> the `rgba()` a gradient stop needs. */
const stop = (hex: string, alpha: number): string => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

/**
 * Fine film-grain overlay (static SVG fractal noise), tiled.
 *
 * MATCHED to `apps/app`: `baseFrequency` 0.55 and `numOctaves` 4, up from 0.82
 * and 2. That is not conformity for its own sake — it serves this file's own
 * stated purpose better than what it replaced. The grain here is a DITHER (see
 * the opacity note on the overlay element), and the banding it has to break up
 * is low-frequency: a 10-20 level ramp spread over ~1400px puts the visible
 * step edges tens of px apart. 0.82 is finer noise than that, so it had little
 * energy where the artefact is; 0.55 with four octaves puts energy across a
 * range of scales including the one that matters. The app reached 0.55 from the
 * other direction — finer noise disappears on a retina display — and both
 * surfaces are viewed on one.
 *
 * DESATURATED, also matched. `feTurbulence` emits COLOUR noise, and this tile
 * previously shipped it raw: under `multiply` on the light page every particle
 * was a coloured speck rather than a neutral one. `feColorMatrix saturate=0`
 * costs nothing and is what the app already does.
 *
 * Still a tiled data URI rather than the app's full-viewport inline `<svg>`.
 * The two are the same picture; the tile is the cheaper way to draw it, and
 * more so here — a viewport-sized `feTurbulence` on a 1280x800 desktop frame is
 * ~3.4x the pixels of one on a 375x812 phone. `stitchTiles` keeps the tile
 * edges from showing as seams.
 *
 * Static in both modes, in both apps. Animating it makes it shimmer, which
 * reads as video noise rather than as a surface, and it is the expensive layer
 * to repaint.
 *
 * ── THIRD CONSUMER: vera-website's `/enter` (VRG-620, 2026-09-21) ─────────
 *
 * The marketing site's sign-in gate reproduces this tile EXACTLY — 180x180,
 * `baseFrequency` 0.55, `numOctaves` 4, `stitchTiles`, `feColorMatrix
 * saturate=0`, light `multiply` at 0.08 — having ported the technique (not the
 * code) from this file. So the grain's PARAMETERS were already identical
 * across all three surfaces before this change, and the only divergence was
 * the dark opacity. See the overlay element below for what moved and why.
 */
const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.55' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"

/**
 * Fixed at module load — the first time any auth page imports this file — and
 * deliberately NOT reset per mount. It is the clock the negative delays are
 * measured against; resetting it would reintroduce the snap it exists to stop.
 */
const AMBIENT_EPOCH = typeof performance !== 'undefined' ? performance.now() : 0

/**
 * The negative delay that lands a freshly-mounted animation at the phase the
 * previous node had reached. `elapsedMs` is measured ONCE PER MOUNT (see
 * `mountElapsed` in SignInBackground), never per render.
 *
 * ⚠️ IT WAS READ PER RENDER UNTIL 2026-09-24, and that was the "blobs jump on
 * a wrong password" bug. Any re-render (the surge context changing on submit,
 * the error arriving) produced a new delay string, emotion swapped the class,
 * and changing `animation-delay` on a RUNNING animation re-seeks it by the
 * difference. Measured on /sign-in, dark: the purple centroid moved 238px in
 * one frame as "Invalid email or password" appeared, against <2px per frame
 * at rest. The delay has to be fixed for the life of the node, which is what
 * "the moment THIS node mounts" always meant.
 */
const phaseDelay = (periodSeconds: number, elapsedMs: number): string =>
  `-${((elapsedMs % (periodSeconds * 1000)) / 1000).toFixed(2)}s`

/**
 * A circular path as four keyframes. Expressed via `translate` (not
 * `transform`) so it composes with the centring below instead of overwriting
 * it, and the radius is resolved through `SCALE` so the drift keeps its
 * relationship to the blob it moves — a 400px orbit against a blob that has
 * grown to 2000px is the same "length from another frame" error as the
 * diameters, and precisely as invisible.
 */
/**
 * A CIRCLE, as a 12-sided path (2026-09-23). The original was a four-point
 * diamond, which was fine for one blob wandering alone; for three lobes that
 * share an orbit it made the triangle visibly square off at the corners.
 * Twelve linear segments at 30° deviate from a true circle by ~1.7% of the
 * radius — invisible under the blur — and need no `offset-path` support.
 */
const orbitKeyframes = (
  radius: number,
  reverse: boolean,
  wobble?: { amp: number; k: number },
) => {
  const frames: Record<string, { translate: string }> = {}
  // 24 steps now, not 12: with a varying radius the path curves more sharply
  // and 30° chords would show as facets.
  const steps = 24
  for (let i = 0; i <= steps; i++) {
    const t = (i * 2 * Math.PI) / steps
    const angle = (reverse ? -1 : 1) * t
    const r = radius + (wobble ? wobble.amp * Math.sin(wobble.k * t) : 0)
    const x = SCALE((r * Math.cos(angle)) / FRAME.w)
    const y = SCALE((r * Math.sin(angle)) / FRAME.w)
    frames[`${((i / steps) * 100).toFixed(3)}%`] = { translate: `${x} ${y}` }
  }
  return frames
}

/**
 * How far the field compresses, and how much faster it runs, at `surge === 1`.
 *
 * The curve lives HERE rather than in the page that triggers it: the page owns
 * the INTENT ("we have arrived"), the backdrop owns what that looks like.
 *
 * 0.42 compression rather than something more dramatic because the blobs are
 * atmosphere. Note that this is a WEAKER guarantee than the legibility note
 * above once used to claim: the bottom blob's core already enters the viewport
 * on its own orbit, so compression is not the only thing that could put a
 * bright object behind the success mark. It is held low for the same reason
 * regardless — that mark is the one place on this page that must stay
 * readable.
 */
const SURGE_COMPRESSION = 0.42
const SURGE_RATE_GAIN = 4

/**
 * How far the field TWISTS as it compresses, in degrees at `surge === 1`.
 *
 * Compression alone pulls every blob along a straight line toward the centre,
 * which reads as the backdrop shrinking. Rotating on the same curve bends those
 * lines into arcs, so the field appears to spiral into the success mark rather
 * than collapse onto it — the difference between "the background moved" and
 * "everything converged HERE".
 *
 * 18deg, not more. This wrapper is viewport-sized and repaints
 * `background.default` to keep the blend target intact (see below), so at
 * scale 0.58 the rotated box still sits well inside the viewport
 * (0.58 * (1280*cos18 + 800*sin18) = 849px against 1280) and the exposed
 * margin is the identical colour that was already exposed by the scale. Push
 * this much past ~25deg and the blobs' own orbits start to read as the whole
 * PAGE tilting, which is a different and much cheaper-looking effect.
 */
const SURGE_SWIRL = 18

/**
 * Keeps --ax / --ay on `node` at the logo's centre, in px relative to `node`.
 * Re-measured on resize, on any DOM change under <body> (route changes swap
 * the auth column), and on a slow interval as a backstop for transitions that
 * move the logo without a mutation. rAF-coalesced, so a burst of mutations is
 * one getBoundingClientRect. Absent logo -> the vars are removed and every blob
 * falls back to its `centre`.
 */
const useLogoAnchor = (ref: React.RefObject<HTMLDivElement | null>) => {
  useLayoutEffect(() => {
    const node = ref.current
    if (!node || typeof window === 'undefined') return
    let frame = 0
    const measure = () => {
      frame = 0
      // The whole form column when a page marks it (Spencer, 2026-09-23: "move
      // the blobs down… centred in the whole section rather than just the
      // logo"), else the logo — pages that have not marked a column keep the
      // halo where it was rather than losing it.
      const logo =
        document.querySelector('[data-auth-column]') ?? document.querySelector('[data-auth-logo]')
      if (!logo) {
        node.style.removeProperty('--ax')
        node.style.removeProperty('--ay')
        return
      }
      const l = logo.getBoundingClientRect()
      // Against the PARENT, not `node`: `node` is the wrapper the surge scales,
      // so its rect shrinks mid-swirl while the blobs' own coordinates do not.
      // The wrapper is `inset: 0` in its parent, so parent px == local px.
      const frameEl = node.parentElement ?? node
      const f = frameEl.getBoundingClientRect()
      // The rects are PAINTED px; --ax/--ay are read back as lengths on `node`, inside
      // `#app`'s text zoom, which scales them again. Divided back to layout px so the
      // pivot sits on the logo at every text size (unconverted, at 0.8 it drifted to
      // 80% of the logo's offset from the frame's corner). See `zoomOf`.
      const z = zoomOf(frameEl)
      node.style.setProperty('--ax', `${((l.left + l.width / 2 - f.left) / z).toFixed(1)}px`)
      node.style.setProperty('--ay', `${((l.top + l.height / 2 - f.top) / z).toFixed(1)}px`)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener('resize', schedule)
    const mo = new MutationObserver(schedule)
    mo.observe(document.body, { childList: true, subtree: true })
    const iv = window.setInterval(schedule, 700)
    return () => {
      window.removeEventListener('resize', schedule)
      mo.disconnect()
      window.clearInterval(iv)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [ref])
}

/** Light-mode reading wash strength (see the element). Tuned with the lobes'
 *  light alphas — the two trade visibility for text contrast. */
const WASH = { core: 0, mid: 0 } as const
/* ⚠️ OFF (core 0 skips the element), 2026-09-23. It was added when the blobs sat at
   the page edges and only strayed behind the column; once the lobes were
   centred ON the form it sat directly over them and light mode lost them
   (lobe-zone peak chroma 5.1 against dark's 21.0). The text contrast it
   protected is now carried by darker light-mode label and link colours
   (authType.ts, the Forgot-password link). Kept as a dial, not deleted: if
   the lobes ever move back to the edges, this is the other half of that
   trade. */

export const SignInBackground = () => {
  const theme = useTheme()
  const isLight = theme.palette.mode === 'light'
  const blend = blendFor(theme.palette.mode)
  const { surge } = useAmbientSurge()
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const fieldRef = useRef<HTMLDivElement | null>(null)
  useLogoAnchor(fieldRef)
  // Frozen at mount so re-renders cannot move the delays — see `phaseDelay`.
  const [mountElapsed] = useState(() =>
    typeof performance === 'undefined' ? 0 : performance.now() - AMBIENT_EPOCH,
  )

  /**
   * The speed-up, via the Web Animations API on the CSS animations declared
   * below — NOT by changing `animation-duration`.
   *
   * `animation-duration` is the obvious lever and it is the wrong one: the
   * running animations are seeded with a negative `animation-delay` measured
   * against `AMBIENT_EPOCH` (see `phaseDelay`), so re-interpreting elapsed time
   * against a new duration lands every blob at a different phase — a visible
   * snap, which is precisely the artefact the epoch machinery exists to remove.
   *
   * `updatePlaybackRate` changes the rate while PRESERVING current time, so the
   * field accelerates smoothly from wherever it happens to be.
   *
   * Scoped with `getAnimations({ subtree: true })` on this element rather than
   * `document.getAnimations()`, so it can only ever touch the blobs.
   *
   * Under reduced motion the boxes below declare `animation: none`, so there
   * are no animations to find and this loop no-ops on an empty list — the guard
   * is belt-and-braces, not the mechanism.
   */
  useEffect(() => {
    const node = fieldRef.current
    if (!node || reduceMotion || typeof node.getAnimations !== 'function') return
    const rate = 1 + SURGE_RATE_GAIN * surge
    node.getAnimations({ subtree: true }).forEach((animation) => {
      if (typeof animation.updatePlaybackRate === 'function') animation.updatePlaybackRate(rate)
      else animation.playbackRate = rate
    })
  }, [surge, reduceMotion])

  return (
  <>
    {/* THE COMPRESSION WRAPPER, and why it repeats `background.default`.

        Scaling this group is what pulls the blobs toward the page centre. The
        catch is `mix-blend-mode`: the blobs blend with their BACKDROP within
        their own stacking context, and today that context is the fixed box in
        `LayoutAuthAmbient` — which is also where `background.default` is
        painted. That is why light mode's `multiply` reads as colour at all.

        A wrapper carrying a `transform` creates a NEW stacking context, so
        without a background of its own the blobs would multiply against
        nothing and light mode would fall back to the pale haze the `blendFor`
        note warns about. Repainting the same colour here keeps the blend
        target identical. Because it is the SAME colour as the box behind it,
        the shrinking edge exposed by the scale is invisible.

        The grain overlay is deliberately a SIBLING, not a child: texture should
        stay uniform across the viewport rather than compressing with the field. */}
    <Box
      ref={fieldRef}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        bgcolor: 'background.default',
        // The swirl tightens around the halo, not the empty screen centre.
        transformOrigin: 'var(--ax, 50%) var(--ay, 50%)',
        // `scale` longhand rather than `transform`, purely for consistency with
        // the blobs' use of `translate`/`scale` longhands below.
        scale: reduceMotion ? '1' : `${1 - SURGE_COMPRESSION * surge}`,
        // The swirl. Same duration and easing as the scale, so the two are one
        // movement — a spiral — rather than a shrink with a spin layered over it.
        rotate: reduceMotion ? '0deg' : `${SURGE_SWIRL * surge}deg`,
        transition: reduceMotion
          ? 'none'
          : 'scale 1600ms cubic-bezier(0.22, 1, 0.36, 1), rotate 1600ms cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'scale, rotate',
      }}
    >
    {BLOBS.map((blob) => {
      const orbitName = `veraOrbit_${blob.key}`
      const breathName = `veraBreath_${blob.key}`
      const morphName = `veraMorph_${blob.key}`
      const diameter = SCALE(blob.size / FRAME.w)
      // Centring as a half-diameter negative margin, NOT `translate(-50%, -50%)`:
      // `translate` belongs to the orbit, and a centring transform would be
      // overwritten by it the moment the animation runs.
      const halfBack = SCALE(-blob.size / 2 / FRAME.w)
      return (
        // OUTER: the orbit. `linear`, so the circle glides at constant speed.
        <Box
          key={blob.key}
          sx={{
            position: 'absolute',
            left: blob.fromLogo
              ? `calc(var(--ax, ${blob.centre.x * 100}%) + ${SCALE(blob.fromLogo.x / FRAME.w)})`
              : `${blob.centre.x * 100}%`,
            top: blob.fromLogo
              ? `calc(var(--ay, ${blob.centre.y * 100}%) + ${SCALE(blob.fromLogo.y / FRAME.w)})`
              : `${blob.centre.y * 100}%`,
            marginLeft: halfBack,
            marginTop: halfBack,
            width: diameter,
            height: diameter,
            zIndex: 1,
            pointerEvents: 'none',
            willChange: 'translate',
            [`@keyframes ${orbitName}`]: orbitKeyframes(blob.orbit.radius, !!blob.orbit.reverse, blob.orbit.wobble),
            animation: `${orbitName} ${blob.orbit.period}s linear infinite`,
            // `phase` puts this lobe a fixed fraction of a lap ahead of the others.
            animationDelay: `calc(${phaseDelay(blob.orbit.period, mountElapsed)} - ${((blob.phase ?? 0) * blob.orbit.period).toFixed(2)}s)`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          {/* INNER: the breath. `ease-in-out`, so the pulse swells rather than
              ticking. Separate element because the two need different easings. */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              // AMORPHOUS, NOT A DISC (2026-09-23). Spencer: "more amorphous
              // blobs… the previous page's made for more negative space in the
              // black parts; here they all blend and it becomes a
              // gradient-shifting background." The fill runs nearly solid to
              // `closest-side` and the blur is small relative to the blob, so a
              // silhouette survives with black around it; `morphName` below
              // melts it. Measured, dark, outside the column: black negative
              // space 3.3% (disc) -> ~24-27%, against 23.7% on the pre-retune
              // page he liked. vera.inc /enter runs the same (enter-ambient.css).
              borderRadius: '42% 58% 63% 37% / 44% 39% 61% 56%',
              background: `radial-gradient(closest-side, ${stop(
                blob.color,
                isLight ? blob.alpha.light : blob.alpha.dark,
              )} 0%, ${stop(
                blob.color,
                (isLight ? blob.alpha.light : blob.alpha.dark) * 0.75,
              )} 55%, ${stop(blob.color, 0)} 100%)`,
              // `multiply` on light, `normal` on dark — the whole reason light
              // mode reads as colour rather than haze. See `blendFor`.
              // Harmless on the transparent part of the gradient: multiplying
              // by nothing leaves the backdrop untouched, so the falloff still
              // fades out rather than banding.
              mixBlendMode: blend,
              // PER-BLOB, and scaling with the blob. Both are deliberate: see
              // the `accent` note for why the three blurs differ, and `SCALE`
              // for why none of them is a px literal. A blur held at 300px
              // while its blob grows to 2000px stops doing the one job a blur
              // has here, which is to keep a circle reading as a wash.
              filter: `blur(${SCALE(blob.blur / FRAME.w)})`,
              willChange: 'scale, opacity, border-radius, rotate',
              [`@keyframes ${morphName}`]: {
                '0%': { borderRadius: '42% 58% 63% 37% / 44% 39% 61% 56%', rotate: '0deg' },
                '33%': { borderRadius: '61% 39% 34% 66% / 57% 64% 36% 43%', rotate: '40deg' },
                '66%': { borderRadius: '35% 65% 55% 45% / 67% 33% 67% 33%', rotate: '70deg' },
                '100%': { borderRadius: '58% 42% 41% 59% / 38% 56% 44% 62%', rotate: '100deg' },
              },
              [`@keyframes ${breathName}`]: {
                '0%': { scale: `${blob.breath.from}`, opacity: blob.breath.opacityFrom },
                '50%': { scale: `${blob.breath.to}`, opacity: blob.breath.opacityTo },
                '100%': { scale: `${blob.breath.from}`, opacity: blob.breath.opacityFrom },
              },
              animation: `${breathName} ${blob.breath.period}s ease-in-out infinite, ${morphName} ${blob.morph}s ease-in-out infinite alternate`,
              // Breath carries the same phase as the orbit: the swell travels.
              animationDelay: `calc(${phaseDelay(blob.breath.period, mountElapsed)} - ${((blob.phase ?? 0) * blob.breath.period).toFixed(2)}s), ${phaseDelay(blob.morph, mountElapsed)}`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          />
        </Box>
      )
    })}
    </Box>
    {/* ── THE READING WASH — light mode only (2026-09-23) ─────────────────
        A soft ellipse of the page colour behind the form column, above the
        blobs. The re-tuned blobs (see BLOBS) brought /enter's edge strength
        here, and in LIGHT the top blob's orbit carries it down behind the
        column: measured worst frame, the EMAIL label fell 4.77 -> 4.39 and
        vera.inc /enter's PASSWORD label to 3.99, under the 4.5 AA the small
        grey text needs. Darkening that text cannot fix /enter without
        inverting its subtle/muted ramp (its own notes record the wall), and
        dimming every blob gives back the presence that was the point. So the
        column gets clean paper and the edges keep their colour. DARK needs
        none: its text is light and rises with the backdrop (5.1+ measured).
        vera.inc /enter carries the identical wash (enter-ambient.css). */}
    {isLight && WASH.core > 0 && (
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          background: `radial-gradient(ellipse 34% 46% at 50% 52%, ${alpha(theme.palette.background.default, WASH.core)} 0%, ${alpha(theme.palette.background.default, WASH.mid)} 55%, transparent 100%)`,
        }}
      />
    )}
    {/* Grain. Same optical problem as the blobs, same shape of answer.
        `overlay` both lightens and darkens around a mid-grey pivot, which is
        what gives the dark page its texture — but on a near-white page the
        lightening half has nowhere to go, so the noise half-disappears and what
        survives reads as dirt rather than grain. Light mode multiplies instead,
        so every grain particle darkens, and at a much lower opacity because
        dark specks on white are far more conspicuous than light specks on
        black: 0.16 ported straight across looks like a dusty screen.

        THIS IS THE ONE PLACE THE TWO APPS' GRAIN DIFFERS, and it is measured.
        `apps/app` uses `overlay` in BOTH modes. That works there because its
        light ground is #FBFAFD, chosen so overlay has something to bite on, and
        even so its grain is nearly cosmetic — the app's own measurement puts it
        at sigma 0.349 of 255. This page's ground is #f4f5f5 and its grain is
        load-bearing (see the opacity note), measuring roughly +/-10 of 255
        under multiply. Reproducing that with overlay would need ~0.88 opacity,
        which then blows out to +/-49 wherever the blobs have darkened the
        ground toward mid-grey, because overlay's gain is 2*(1-backdrop) and so
        varies across exactly the gradient the grain is there to smooth.
        Uniform amplitude is the whole point of a dither. */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        // DARK RAISED 0.16 -> 0.24 (VRG-620, 2026-09-21). This is the ONE value
        // taken from vera-website's `/enter`, and it is taken because this
        // page's own banding says so — not because the other surface uses it.
        //
        // MEASURED here, on THIS ground, with the metric `/enter` itself used:
        // the longest run of one identical channel value down a column clear of
        // the form, worst over a wall-clock sweep of the orbits.
        //
        // | frame      | 0.16 (was) | 0.24 (now) | light 0.08, for scale |
        // |------------|------------|------------|-----------------------|
        // | dark 1280  |    31px    |    19px    |         10px          |
        // | dark  375  |    28px    |    23px    |         11px          |
        //
        // 0.16 left dark banding THREE TIMES light's, on the same page, under
        // the same tile — so the mode that needs the dither most was getting
        // the least of it. That is this file's own rule ("below roughly one
        // quantisation step of amplitude it cannot [dither]") failing in the
        // direction the light-mode note already caught once, and it went
        // unnoticed because dark's plateaus are low-contrast and read as
        // smoothness rather than as steps.
        //
        // ⚠️ It is NOT a straight port. `/enter` reached 0.24 on a #0b0b0f
        // ground; this page's is #09090b, and `overlay`'s gain is 2*backdrop,
        // so the DARKER ground here has LESS to bite on and needs at least as
        // much. The arithmetic runs the same way for both, which is why the two
        // land on the same number — agreement, not transcription.
        //
        // Costs 0.08:1 of contrast at the tightest dark row (6.76 -> 6.68),
        // against a 4.5 threshold it still clears by 48%. Chroma is unmoved
        // (11.51 -> 11.60) because the tile is desaturated: a dither must not
        // tint.
        //
        // LIGHT RAISED 0.04 -> 0.08. 0.04 was tuned for "don't let dark specks
        // read as dirt on white", which it achieved by being too faint to do the
        // job grain is actually here for.
        //
        // Grain's purpose is DITHERING, not texture. 8-bit colour gives 256
        // levels per channel, and these blobs ramp maybe 10-20 levels across
        // ~1400px, so the gradient quantises into visibly wide bands. Noise
        // breaks the step edges up and the eye integrates them back into a
        // smooth ramp. Below roughly one quantisation step of amplitude it
        // cannot do that, and 0.04 multiply on a near-white page is under it —
        // so light mode had grain that cost a composite layer and dithered
        // nothing, which is why it still read as banded.
        //
        // Light stays well below dark's 0.16 for the reason the note above
        // gives: multiply makes every particle DARKEN, and dark specks on white
        // are far more conspicuous than light specks on black.
        // Raised 2026-09-23 (was 0.08 / 0.24). Spencer: "can we add some more
        // grain". With the page now mostly black, the grain is the texture of
        // the negative space; vera.inc /enter carries the same values.
        opacity: isLight ? 0.13 : 0.4,
        mixBlendMode: isLight ? 'multiply' : 'overlay',
        backgroundImage: GRAIN_URI,
        backgroundRepeat: 'repeat',
      }}
    />
  </>
  )
}
